import { Combobox, DatePickerDemo } from '@alphabait/ui'
import { FormProps } from '@alphabait/ui/form'
import { format } from 'date-fns'
import { Control, useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { DateTimeInput } from './custom-types'
import styles from './form.module.css'

// Legacy props (form module)
type LegacyProps = {
  field: DateTimeInput
  formProps: FormProps
}

// Direct props (react-hook-form)
type DirectProps = {
  name: string
  control: Control<any>
  label?: string
  testId?: string
  className?: string
}

type CustomDateTimeProps = LegacyProps | DirectProps

function isLegacyProps(props: CustomDateTimeProps): props is LegacyProps {
  return 'field' in props && 'formProps' in props
}

export default function CustomDateTime(props: CustomDateTimeProps) {
  // Extract props based on mode
  const fieldName = isLegacyProps(props) ? props.field.name : props.name
  const control = isLegacyProps(props) ? props.formProps.control : props.control
  const testId = isLegacyProps(props) ? props.field.name : props.testId || props.name
  const dateClassName = isLegacyProps(props) ? props.field.uiOptions?.className : props.className
  const hours = Array.from({ length: 24 }, (_, i) =>
    i < 10 ? `0${i}` : i.toString(),
  )
  const minutes = Array.from({ length: 60 }, (_, i) =>
    i < 10 ? `0${i}` : i.toString(),
  )

  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    control,
    name: fieldName,
  })

  const { t } = useTranslation()

  const dateValue =
    value ??
    (isLegacyProps(props) ? props.formProps.formValues[fieldName] : undefined) ??
    new Date().toISOString()
  const localDate = format(new Date(dateValue), 'yyyy-MM-dd HH:mm')

  const [d, time] = localDate.split(' ')
  const [h, m] = time.split(':')

  const handleDateChange = (newDate: string) => {
    const combinedDateTime = `${newDate}T${h}:${m}:00`
    onChange(new Date(combinedDateTime).toISOString())
  }

  const handleHourChange = (newHour: string) => {
    const combinedDateTime = `${d}T${newHour}:${m}:00`
    onChange(new Date(combinedDateTime).toISOString())
  }

  const handleMinuteChange = (newMinute: string) => {
    const combinedDateTime = `${d}T${h}:${newMinute}:00`
    onChange(new Date(combinedDateTime).toISOString())
  }

  return (
    <div>
      <div data-testid={testId} className="flex gap-2">
        <div className={dateClassName}>
          <DatePickerDemo
            value={d}
            setValue={(newDate) => handleDateChange(newDate.substring(0, 10))}
          />
        </div>
        <div className="flex-col">
          <div className="flex gap-2">
            <div className="w-24">
              <Combobox
                type="single"
                value={h}
                placeholder="Hour"
                className="w-full border border-border"
                setValue={(newHour) => {
                  if (newHour) {
                    handleHourChange(newHour)
                  }
                }}
                options={hours.map((hour) => ({
                  value: hour,
                  label: hour,
                }))}
              />
            </div>

            <div className="w-24">
              <Combobox
                type="single"
                value={m}
                placeholder="Minute"
                className="w-full border border-border"
                setValue={(newMinute) => {
                  if (newMinute) {
                    handleMinuteChange(newMinute)
                  }
                }}
                options={minutes.map((minute) => ({
                  value: minute,
                  label: minute,
                }))}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="my-auto py-1 text-sm text-foreground">
        {format(new Date(dateValue), 'yyyy-MM-dd HH:mm')}(
        {Intl.DateTimeFormat().resolvedOptions().timeZone})
      </div>
      {error && (
        <span
          className={styles.errorMessage}
          data-testid={`${testId}-error`}
        >
          {t(`validation_messages.${error.message}`)}
        </span>
      )}
    </div>
  )
}
